import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Button,
  Typography,
  Stack,
  Select,
  InputLabel,
  FormControl,
  MenuItem,
  Chip,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";

import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import LabelIcon from "@mui/icons-material/Label";
import ViewListIcon from "@mui/icons-material/ViewList";

import MaterialsTab from "./MaterialsTab";
import { TreeView, TreeItem } from "@mui/x-tree-view";
import { usePiping } from "../../PipingContext";

export default function Validation() {
  const theme = useTheme();

  const [selectedListType, setSelectedListType] = useState("tagged");
  const [fromValue, setFromValue]               = useState("");

  // Single data pair — mode drives column interpretation
  const [leftDict,  setLeftDict]  = useState({});
  const [rightDict, setRightDict] = useState({});
  const [toggleCounter, setToggleCounter] = useState(0);


  
  

  // Tree
  const [treeData,      setTreeData]      = useState([]);
  const [checkedLeaves, setCheckedLeaves] = useState({});
  const [foundFiles,    setFoundFiles]    = useState({});
  const [dxfFiles,      setDxfFiles]      = useState([]);

  // Loading
  const [isBrowsing,   setIsBrowsing]   = useState(false);
  const [isMakingList, setIsMakingList] = useState(false);

  const { extractPnidForValidation } = usePiping();

  /* ── Tree helpers ─────────────────────────────────────────────── */
  const isLeaf  = (node) => !node.children || node.children.length === 0;
  const leafKey = (node, parentLabel) =>
    parentLabel ? `${parentLabel}|${node.label}` : node.label;

  const toggleLeaf = (key) =>
    setCheckedLeaves((prev) => ({ ...prev, [key]: !prev[key] }));

  const collectLeafKeys = (nodes, parent = null, acc = new Set()) => {
    nodes.forEach((node) => {
      const leaf = !node.children || node.children.length === 0;
      if (leaf) acc.add(parent ? `${parent}|${node.label}` : node.label);
      else collectLeafKeys(node.children, node.label, acc);
    });
    return acc;
  };

  const buildSelectionPayload = () => {
    const result   = [];
    const leafKeys = collectLeafKeys(treeData);
    Object.entries(checkedLeaves).forEach(([key, checked]) => {
      if (!checked || !leafKeys.has(key)) return;
      if (!key.includes("|")) {
        result.push(key);
      } else {
        const [parent, child] = key.split("|");
        let existing = result.find((r) => typeof r === "object" && r[parent]);
        if (!existing) { existing = {}; result.push(existing); }
        existing[parent] = child;
      }
    });
    return result;
  };

  /* ── Actions ──────────────────────────────────────────────────── */
  function normalizeDynamicData(source) {
    const result = {};
    let i = 1;
    // Handle both array and plain-object/dict shapes coming from the backend
    const rows = Array.isArray(source) ? source : Object.values(source ?? {});
    rows.forEach((row) => { result[`row${i++}`] = { ...row }; });
    return result;
  }

  const MakelistButtonHandle = async () => {
    setIsMakingList(true);
    let converted = {};
    try {
      if (fromValue === "P&ID") {
        const nodes = buildSelectionPayload();
        const paths = nodes
          .map((name) => dxfFiles.find((f) => f.fileName === name)?.filePath)
          .filter(Boolean);
        let trimList = [];
        for (const p of paths) {
          try {
            const res = await extractPnidForValidation(p);
            trimList = res?.Trimlist ?? res ?? [];
          } catch (e) { console.error("P&ID extract error:", e); }
        }
        converted = normalizeDynamicData(
          trimList.map((item) => ({ TagNumber: item.TagNumber, Component: item.Component }))
        );
      } else {
        const res = await window.electron.piping.Pipevalidationmakelist(
          selectedListType === "tagged",
          fromValue,
          buildSelectionPayload()
        );
        // res.data may be an array or an object — normalizeDynamicData handles both
        converted = normalizeDynamicData(res?.data ?? res ?? {});
      }

      // Use functional updater to avoid stale closure on toggleCounter
      setToggleCounter((c) => {
        if (c % 2 === 0) setLeftDict(converted);
        else             setRightDict(converted);
        return c + 1;
      });
    } catch (err) {
      console.error("MakeList error:", err);
    } finally {
      setIsMakingList(false);
    }
  };

  const BrowseButtonHandle = async () => {
    // Clear tree immediately so the user sees a fresh state on every browse
    setTreeData([]);
    setCheckedLeaves({});
    setIsBrowsing(true);
    try {
      if (fromValue === "P&ID") {
        const folder = await window.electron?.openFolderDialog();
        if (!folder) return;
        const files = await window.electron.searchFilesInDirectory(folder, ".dxf");
        setDxfFiles(files);
        if (files.length > 0) {
          setTreeData(buildTreeFromFiles(files.map((f) => f.fileName)));
          setCheckedLeaves({});
        }
      } else {
        const folder = await window.electron.browseFolder();
        if (!folder) return;
        const result = await window.electron.piping.Pipevalidationbrowse(
          selectedListType === "tagged",
          fromValue,
          folder
        );
        setTreeData(buildTreeFromFiles(result.data));
        setCheckedLeaves({});
        if (result.foundFiles) setFoundFiles(result.foundFiles);
      }
    } catch (err) {
      console.error("Browse error:", err);
    } finally {
      setIsBrowsing(false);
    }
  };

  /* ── Tree construction ────────────────────────────────────────── */
  const buildTreeFromFiles = (files) => {
    let c = 1;
    return files.map((item) => {
      if (typeof item === "string")
        return { id: String(c++), label: item, children: [] };
      if (typeof item === "object") {
        const key = Object.keys(item)[0];
        return {
          id: String(c++), label: key,
          children: item[key].map((s) => ({ id: String(c++), label: s })),
        };
      }
      return null;
    }).filter(Boolean);
  };

  const checkedCount = Object.values(checkedLeaves).filter(Boolean).length;

  const renderTreeLeaves = (node, parentLabel = null) => {
    const leaf      = isLeaf(node);
    const key       = leaf ? leafKey(node, parentLabel) : null;
    const isChecked = leaf && !!checkedLeaves[key];

    return (
      <TreeItem
        key={node.id}
        nodeId={node.id}
        sx={{
          "& .MuiTreeItem-content": {
            borderRadius: "6px", py: 0.25,
            "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.12) },
            "&.Mui-selected, &.Mui-selected.Mui-focused": {
              backgroundColor: alpha(theme.palette.primary.main, 0.18),
            },
          },
          "& .MuiTreeItem-group": {
            borderLeft: `1px dashed ${alpha(theme.palette.primary.main, 0.45)}`,
            ml: 1.5, pl: 1,
          },
        }}
        label={
          <Box display="flex" alignItems="center" gap={0.75} py={0.25}>
            {leaf && (
              <Box
                component="span"
                onClick={(e) => { e.stopPropagation(); toggleLeaf(key); }}
                sx={{
                  width: 15, height: 15, borderRadius: "3px", flexShrink: 0,
                  cursor: "pointer", transition: "all 0.15s ease",
                  border: `1.5px solid ${isChecked ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.60)}`,
                  backgroundColor: isChecked ? theme.palette.primary.main : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  "&::after": isChecked ? {
                    content: '""', width: 4, height: 7,
                    border: "1.5px solid white", borderTop: "none", borderLeft: "none",
                    transform: "rotate(45deg) translateY(-1px)",
                  } : {},
                }}
              />
            )}
            <Typography variant="body2" sx={{
              fontSize: "0.75rem",
              fontWeight: leaf ? (isChecked ? 600 : 400) : 600,
              color: leaf ? (isChecked ? theme.palette.primary.main : "text.primary") : "text.primary",
              letterSpacing: leaf ? 0 : "0.01em",
            }}>
              {node.label}
            </Typography>
          </Box>
        }
      >
        {node.children?.map((child) => renderTreeLeaves(child, node.label))}
      </TreeItem>
    );
  };

  const SectionLabel = ({ children }) => (
    <Typography variant="caption" sx={{
      textTransform: "uppercase", letterSpacing: "0.08em",
      fontWeight: 700, color: alpha(theme.palette.primary.main, 0.88), fontSize: "0.65rem",
    }}>
      {children}
    </Typography>
  );

  /* ── Render ───────────────────────────────────────────────────── */
  return (
    <Box sx={{ p: 2, minHeight: "100vh", backgroundColor: "#eef0fa" }}>
      <Grid container spacing={2} alignItems="stretch">

        {/* ── LEFT CONTROL PANEL ── */}
        <Grid item xs={12} md={4} lg={3}>
          <Paper elevation={0} sx={{
            p: 2, height: "100%", display: "flex", flexDirection: "column", gap: 2,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.45)}`,
            borderRadius: 2, backgroundColor: "#fff",
          }}>

            {/* List Type — ToggleButtonGroup */}
            <Box>
              <SectionLabel>List Type</SectionLabel>
              <ToggleButtonGroup
                value={selectedListType}
                exclusive
                onChange={(_, val) => {
                  if (!val) return;
                  setSelectedListType(val);
                  // Clear tables and reset toggle counter on mode switch
                  setLeftDict({});
                  setRightDict({});
                  setToggleCounter(0);
                  // Clear P&ID source option when switching to bulk
                  if (val === "bulk" && fromValue === "P&ID") setFromValue("");
                }}
                size="small"
                sx={{
                  mt: 0.75, width: "100%",
                  "& .MuiToggleButtonGroup-grouped": {
                    flex: 1,
                    border: `1.5px solid ${alpha(theme.palette.primary.main, 0.38)} !important`,
                    borderRadius: "20px !important",
                    mx: "3px !important",
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    textTransform: "none",
                    color: alpha(theme.palette.primary.main, 0.70),
                    py: 0.6,
                    gap: 0.5,
                    transition: "all 0.15s ease",
                    "&.Mui-selected": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.15),
                      borderColor: `${theme.palette.primary.main} !important`,
                      color: theme.palette.primary.main,
                      "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.45) },
                    },
                    "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.18) },
                  },
                }}
              >
                <ToggleButton value="tagged">
                  <LabelIcon sx={{ fontSize: 13 }} />
                  Tagged
                </ToggleButton>
                <ToggleButton value="bulk">
                  <ViewListIcon sx={{ fontSize: 13 }} />
                  Bulk
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Divider sx={{ borderColor: alpha(theme.palette.primary.main, 0.15) }} />

            {/* Source */}
            <Box>
              <SectionLabel>Source</SectionLabel>
              <FormControl fullWidth size="small" sx={{ mt: 0.75 }}>
                <InputLabel sx={{
                  fontSize: "0.75rem",
                  color: alpha(theme.palette.primary.main, 0.80),
                  "&.Mui-focused": { color: theme.palette.primary.main },
                }}>
                  From
                </InputLabel>
                <Select
                  label="From"
                  value={fromValue}
                  onChange={(e) => setFromValue(e.target.value)}
                  sx={{
                    fontSize: "0.78rem", borderRadius: 1.5,
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: alpha(theme.palette.primary.main, 0.38) },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: alpha(theme.palette.primary.main, 0.70) },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.primary.main },
                  }}
                >
                  {selectedListType === "tagged" && (
                    <MenuItem value="P&ID" sx={{ fontSize: "0.78rem" }}>P&amp;ID</MenuItem>
                  )}
                  <MenuItem value="3D Models"   sx={{ fontSize: "0.78rem" }}>3D Models</MenuItem>
                  <MenuItem value="2D Drawings" sx={{ fontSize: "0.78rem" }}>2D Drawings</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Action buttons */}
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={BrowseButtonHandle}
                disabled={isBrowsing || isMakingList || !fromValue}
                startIcon={
                  isBrowsing
                    ? <CircularProgress size={12} thickness={5} sx={{ color: "inherit" }} />
                    : <FolderOpenIcon sx={{ fontSize: "14px !important" }} />
                }
                sx={{
                  flex: 1, fontSize: "0.72rem", fontWeight: 600, borderRadius: 1.5,
                  textTransform: "none",
                  borderColor: alpha(theme.palette.primary.main, 0.60),
                  color: theme.palette.primary.main,
                  "&:hover": { borderColor: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.18) },
                }}
              >
                {isBrowsing ? "Browsing…" : "Browse"}
              </Button>
              <Button
                variant="contained"
                onClick={MakelistButtonHandle}
                disabled={isBrowsing || isMakingList || checkedCount === 0}
                startIcon={
                  isMakingList
                    ? <CircularProgress size={12} thickness={5} sx={{ color: "inherit" }} />
                    : <PlaylistAddIcon sx={{ fontSize: "14px !important" }} />
                }
                disableElevation
                sx={{
                  flex: 1, fontSize: "0.72rem", fontWeight: 700, borderRadius: 1.5,
                  textTransform: "none",
                  backgroundColor: theme.palette.primary.main,
                  "&:hover": { backgroundColor: "#3336a0" },
                }}
              >
                {isMakingList ? "Extracting…" : "Extract"}
              </Button>
            </Stack>

            <Divider sx={{ borderColor: alpha(theme.palette.primary.main, 0.15) }} />

            {/* File tree */}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.75}>
                <SectionLabel>Files</SectionLabel>
                {checkedCount > 0 && (
                  <Chip
                    label={`${checkedCount} selected`}
                    size="small"
                    sx={{
                      height: 18, fontSize: "0.62rem", fontWeight: 700,
                      backgroundColor: alpha(theme.palette.primary.main, 0.18),
                      color: theme.palette.primary.main,
                      "& .MuiChip-label": { px: 1 },
                    }}
                  />
                )}
              </Box>
              <Paper variant="outlined" sx={{
                p: 1, maxHeight: 300, overflow: "auto", borderRadius: 1.5,
                borderColor: alpha(theme.palette.primary.main, 0.45),
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                "&::-webkit-scrollbar": { width: 4 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.38), borderRadius: 2,
                },
              }}>
                {treeData.length === 0 ? (
                  <Box sx={{ py: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75 }}>
                    <FolderOpenIcon sx={{ fontSize: 28, color: alpha(theme.palette.primary.main, 0.38) }} />
                    <Typography variant="caption" sx={{
                      color: alpha(theme.palette.primary.main, 0.60), fontSize: "0.68rem",
                    }}>
                      Browse a folder to load files
                    </Typography>
                  </Box>
                ) : (
                  <TreeView defaultExpandAll>
                    {treeData.map((node) => renderTreeLeaves(node))}
                  </TreeView>
                )}
              </Paper>
            </Box>

          </Paper>
        </Grid>

        {/* ── RIGHT CONTENT PANEL ── */}
        <Grid item xs={12} md={8} lg={9}>
          <Paper elevation={0} sx={{
            height: "100%", display: "flex", flexDirection: "column",
            border: `1px solid ${alpha(theme.palette.primary.main, 0.45)}`,
            borderRadius: 2, backgroundColor: "#fff", overflow: "hidden",
          }}>
            {/* Mode indicator header */}
            <Stack direction="row" alignItems="center" sx={{
              px: 2, minHeight: 40,
              borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              gap: 1,
            }}>
              {selectedListType === "tagged"
                ? <LabelIcon sx={{ fontSize: 14, color: theme.palette.primary.main }} />
                : <ViewListIcon sx={{ fontSize: 14, color: theme.palette.primary.main }} />
              }
              <Typography sx={{
                fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.01em",
                color: theme.palette.primary.main,
              }}>
                {selectedListType === "tagged" ? "Tagged Materials" : "Bulk Materials"}
              </Typography>

            </Stack>

            <Box sx={{ p: 2, flexGrow: 1, overflow: "auto" }}>
              <MaterialsTab
                key={selectedListType}
                mode={selectedListType}
                leftData={leftDict}
                rightData={rightDict}
                setLeftDict={setLeftDict}
                setRightDict={setRightDict}
                removeLeftTable={() => setLeftDict({})}
                removeRightTable={() => setRightDict({})}
                isLoading={isMakingList}
              />
            </Box>
          </Paper>
        </Grid>

      </Grid>
    </Box>
  );
}
